import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

// Live mobile support room details — jab bhi ye timing/link change ho, sirf yahan update karna.
const SUPPORT_INFO = {
  timing: "Everyday - 10:30am – 7:30pm",
  meetLink: "https://meet.google.com/xvm-mxxt-vqa",
};

/**
 * LiveSupportWidget
 * ---------------------------------------------------------------------------
 * Header ke topbar wala "Live Support" icon + dropdown — apna open/close
 * state aur click-outside/Escape handling khud manage karta hai, taaki
 * Header.jsx is feature ke liye extra state/refs track na kare.
 * ---------------------------------------------------------------------------
 */
const LiveSupportWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);

  // Support executive contact details — cached in localStorage at login
  // (Login.jsx, from the GetEmployeeByPhoneNumber response) so this widget
  // just reads the cache instead of calling the API itself.
  const [executive] = useState(() => ({
    name: localStorage.getItem("ExecutiveName") || "",
    phone: localStorage.getItem("ExecutivePhone") || "",
    email: localStorage.getItem("ExecutiveEmail") || "",
  }));
  const hasExecutiveInfo = executive.name || executive.phone || executive.email;
  const executiveInitials = executive.name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('');

  const handleConnect = () => {
    window.open(SUPPORT_INFO.meetLink, '_blank', 'noopener,noreferrer');
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('click', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  return (
    <li className="nav-item topbar-icon dropdown hidden-caret" ref={wrapperRef}>
      <Link
        className="nav-link dropdown-toggle"
        to="#"
        role="button"
        title="Live Support"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((prev) => !prev)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '44px',
          height: '44px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #f4a900, #d98c00)',
          boxShadow: '0 4px 10px rgba(244, 169, 0, 0.35)',
        }}
      >
        <i className="bx bx-video" style={{ fontSize: '20px', color: '#fff' }} />
      </Link>
      <ul
        className={`dropdown-menu dropdown-support animated fadeIn ${isOpen ? 'show' : ''}`}
        style={{ right: 0, left: 'auto', minWidth: '270px', padding: '16px', border: 0, boxShadow: '0 10px 30px rgba(6, 19, 61, 0.18)' }}
      >
        <li style={{ listStyle: 'none' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
            <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#2ecc71', display: 'inline-block' }} />
            <h6 style={{ margin: 0, fontWeight: 700, color: '#06133d' }}>Live Support Room</h6>
          </div>
          <p style={{ margin: '6px 0 14px', fontSize: '13px', color: '#6b7280' }}>{SUPPORT_INFO.timing}</p>
          <button
            type="button"
            onClick={handleConnect}
            style={{
              display: 'block',
              width: '100%',
              textAlign: 'center',
              padding: '10px 12px',
              background: 'linear-gradient(135deg, #32438e, #06133d)',
              color: '#ffffff',
              border: 0,
              borderRadius: '8px',
              fontWeight: 600,
              fontSize: '14px',
              cursor: 'pointer',
            }}
          >
            Connect
          </button>

          {hasExecutiveInfo && (
            <div style={{ marginTop: '14px', paddingTop: '12px', borderTop: '1px solid #eef0f4' }}>
              <p style={{ margin: '0 0 10px', fontSize: '12px', color: '#6b7280', lineHeight: 1.4 }}>
                For any support related need, please call your Executive below, or connect via the Live Support Room above.
              </p>

              <div style={{ background: '#f7f8fc', borderRadius: '10px', padding: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div
                    style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #32438e, #06133d)',
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 700,
                      fontSize: '13px',
                      flexShrink: 0,
                    }}
                  >
                    {executiveInitials || <i className="bx bx-user" style={{ fontSize: '16px' }} />}
                  </div>
                  <div style={{ minWidth: 0 }}>
                    {executive.name && (
                      <p style={{ margin: 0, fontWeight: 600, fontSize: '13px', color: '#06133d' }}>{executive.name}</p>
                    )}
                    <p style={{ margin: '2px 0 0', fontSize: '11px', color: '#6b7280' }}>Executive</p>
                  </div>
                </div>

                {(executive.phone || executive.email) && (
                  <div
                    style={{
                      marginTop: '10px',
                      paddingTop: '10px',
                      borderTop: '1px solid #e5e7eb',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '6px',
                    }}
                  >
                    {executive.phone && (
                      <a
                        href={`tel:${executive.phone}`}
                        style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#374151', textDecoration: 'none' }}
                      >
                        <i className="bx bx-phone" style={{ color: '#32438e', fontSize: '15px' }} />
                        {executive.phone}
                      </a>
                    )}
                    {executive.email && (
                      <a
                        href={`mailto:${executive.email}`}
                        style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#374151', textDecoration: 'none' }}
                      >
                        <i className="bx bx-envelope" style={{ color: '#32438e', fontSize: '15px' }} />
                        {executive.email}
                      </a>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </li>
      </ul>
    </li>
  );
};

export default LiveSupportWidget;
